import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { getSpeedStats, getStudentStats } from "../../../services/moodle/ranking";
import { getUserDoubtStats } from "../../../services/moodle/estudio";
import { createLogger } from "../../../utils/debug";
import BarChartIcon from "@mui/icons-material/BarChart";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BoltIcon from "@mui/icons-material/Bolt";
import PsychologyIcon from "@mui/icons-material/Psychology";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import BalanceIcon from "@mui/icons-material/Balance";
import { HelpTooltip } from "../../../components/HelpTooltip";
import "./index.css";

const log = createLogger("DetailedStats");
const EMPTY_MESSAGE = "Aún no hay datos que mostrar.";
const ENDPOINT_ERROR = "No se pudieron cargar estos datos.";
const initialEndpointState = () => ({ data: null, loading: true, error: null });

const ADVICE_MAP = {
  advicepending: "Responde y finaliza tus preguntas marcadas para recibir consejos personalizados.",
  advicebelow50: "No te la juegues el día del examen. Tu intuición suele traducirse en errores.",
  advice50to64: "Si el día del examen has respondido las justas no te la juegues. Es un cara o cruz.",
  advice65plus: "¡Juégatela el día del examen! Te favorece.",
};

const SpeedChart = ({ mine = 0, cohort = 0 }) => {
  const max = 60;
  const ticks = [60, 50, 40, 30, 20, 10, 0];
  const plotH = 132;
  const barH = (value) => (Math.max(0, Math.min(max, value)) / max) * plotH;
  return (
    <svg className="ds-speedchart" viewBox="0 0 220 170" width="240" height="170">
      {ticks.map((tick) => {
        const y = 8 + ((max - tick) / max) * plotH;
        return (
          <g key={tick}>
            <line x1="30" y1={y} x2="210" y2={y} stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <text x="24" y={y + 4} textAnchor="end" className="ds-axis">
              {tick}
            </text>
          </g>
        );
      })}
      <rect x="70" y={8 + plotH - barH(mine)} width="42" height={barH(mine)} fill="#e2e1f4" />
      <rect x="140" y={8 + plotH - barH(cohort)} width="42" height={barH(cohort)} fill="#ecb236" />
    </svg>
  );
};

const fmtSeconds = (value) => `${Number(value).toFixed(2).replace(".", ",")}`;
const fmtPercentile = (value) => `${Math.round(value)}`;
const fmtGpa = (value) => Number(value).toFixed(2).replace(".", ",");

const endpointErrorState = (message) => ({ data: null, loading: false, error: new Error(message) });

const StatCard = ({
  icon,
  title,
  tooltip,
  endpoint,
  available,
  value,
  format,
  unit,
  contextNote,
  inlineContextNote = false,
  expanded,
  onToggleExpand,
  children,
}) => {
  const hasData = !endpoint.loading && !endpoint.error && available && value != null;

  return (
    <div className={`ds-card ${expanded ? "is-expanded" : ""}`}>
      <div className="ds-card-main">
        <div className="ds-card-icon-wrap" aria-hidden="true">
          {icon}
        </div>
        <div className="ds-card-body">
          <div className="ds-card-title-row">
            <h3 className="ds-card-title">{title}</h3>
            {tooltip && (
              <HelpTooltip
                text={tooltip}
                ariaLabel={`Más información sobre ${title}`}
                className="ds-title-help"
              />
            )}
          </div>

          <div className="ds-card-value-row">
            {endpoint.loading ? (
              <span className="ds-empty">Cargando…</span>
            ) : endpoint.error ? (
              <span className="ds-empty ds-empty-error">{ENDPOINT_ERROR}</span>
            ) : !available || value == null ? (
              <span className="ds-empty">{EMPTY_MESSAGE}</span>
            ) : (
              <div className={inlineContextNote ? "ds-inline-metric" : "flex flex-col"}>
                <div className="flex items-baseline gap-2">
                  <span className="ds-bignum">{format(value)}</span>
                  {unit && <span className="ds-unit">{unit}</span>}
                </div>
                {contextNote && <div className="ds-context-note">{contextNote}</div>}
              </div>
            )}
          </div>

          <div className="ds-card-actions">
            <button
              type="button"
              onClick={onToggleExpand}
              disabled={!hasData}
              className={`ds-details-btn ${!hasData ? "is-disabled" : ""}`}
            >
              <BarChartIcon sx={{ fontSize: 18 }} />
              <span>{expanded ? "Ocultar detalles" : "Ver detalles"}</span>
            </button>
          </div>
        </div>
      </div>

      {hasData && expanded && (
        <div className="ds-card-expansion">
          {children}
        </div>
      )}
    </div>
  );
};

export const DetailedStats = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem("token");
  const userid = user?.id;
  const [progress, setProgress] = useState(initialEndpointState);
  const [speed, setSpeed] = useState(initialEndpointState);
  const [doubtStats, setDoubtStats] = useState(initialEndpointState);
  const [activeTab, setActiveTab] = useState("performance");
  const [expandedCards, setExpandedCards] = useState({
    percentile: false,
    gpa: false,
    doubts: false,
    doubtFrequency: false,
    precision: false,
    ratio: false,
    tests: false,
    speed: false,
  });

  const toggleExpand = (key) => {
    setExpandedCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    let cancelled = false;
    if (!token || !userid) {
      const noSession = endpointErrorState("No hay sesión activa.");
      setProgress(noSession);
      setSpeed(noSession);
      setDoubtStats(noSession);
      return () => {
        cancelled = true;
      };
    }
    setProgress(initialEndpointState());
    setSpeed(initialEndpointState());
    setDoubtStats(initialEndpointState());
    log.info("fetching knowledge metrics", { userid });
    getStudentStats({ token, userid, courseid: 0 })
      .then((data) => !cancelled && setProgress({ data, loading: false, error: null }))
      .catch((error) => {
        if (!cancelled) {
          log.error("progress stats fetch failed", error);
          setProgress({ data: null, loading: false, error });
        }
      });
    getSpeedStats({ token, userid, courseid: 0 })
      .then((data) => !cancelled && setSpeed({ data, loading: false, error: null }))
      .catch((error) => {
        if (!cancelled) {
          log.error("speed stats fetch failed", error);
          setSpeed({ data: null, loading: false, error });
        }
      });
    getUserDoubtStats({ token })
      .then((data) => !cancelled && setDoubtStats({ data, loading: false, error: null }))
      .catch((error) => {
        if (!cancelled) {
          log.error("doubt stats fetch failed", error);
          setDoubtStats({ data: null, loading: false, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, userid]);

  // `no_contestadas` is deliberately excluded: only completed answers prove progress evidence.
  const acertadas = Number(progress.data?.acertadas || 0);
  const falladas = Number(progress.data?.falladas || 0);
  const answeredCount = progress.data ? acertadas + falladas : 0;
  const hasProgressEvidence = answeredCount > 0;
  const accuracyPct = hasProgressEvidence ? Math.round((acertadas / answeredCount) * 100) : 0;
  const ratioAciertosFallo = falladas > 0
    ? (acertadas / falladas).toFixed(1).replace(".", ",")
    : acertadas > 0
    ? `${acertadas},0`
    : "0,0";

  // Doubt / "Me la Juego" metrics directly from local_tipddy_pregunta_dudosa
  const doubtsCorrect = Number(doubtStats.data?.doubts_correct || 0);
  const doubtsIncorrect = Number(doubtStats.data?.doubts_incorrect || 0);
  const doubtsMarked = Number(doubtStats.data?.total_doubts_marked || 0);
  const doubtsPending = Number(doubtStats.data?.doubts_pending || 0);
  const doubtsResolved = doubtsCorrect + doubtsIncorrect;
  const hasDoubtEvidence = doubtsResolved > 0 || (doubtStats.data && doubtsMarked > 0);
  const doubtSuccessRate = doubtsResolved > 0
    ? Math.round(Number(doubtStats.data?.success_rate_on_doubts ?? (doubtsCorrect / doubtsResolved) * 100))
    : (doubtStats.data?.success_rate_on_doubts != null ? Math.round(Number(doubtStats.data.success_rate_on_doubts)) : 0);
  const doubtFrequencyPct = hasProgressEvidence
    ? Math.round((doubtsMarked / answeredCount) * 100)
    : 0;
  const testsDone = Number(speed.data?.my_tests_done || doubtStats.data?.total_quizzes_attempted || 0);
  const doubtsPerTest = testsDone > 0 ? (doubtsMarked / testsDone).toFixed(1).replace(".", ",") : "0,0";
  const answeredSpeed = Number(speed.data?.my_answered || 0);
  const hasTests = testsDone > 0;
  const hasSpeedEvidence = answeredSpeed > 0;

  return (
    <section className="detailed-stats">
      <div className="ds-header justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="ds-header-icon text-md-primary-container flex items-center justify-center shrink-0" role="img" aria-label="conocimientos">
            <BarChartIcon sx={{ fontSize: 32 }} />
          </span>
          <h2>
            <strong>Conocimientos</strong> <span className="ds-sep">|</span>{" "}
            <span className="ds-header-subtitle">Mis estadísticas globales</span>
          </h2>
        </div>
        <span className="ds-tagline hidden tablet:inline">Tu aprendizaje en números.</span>
      </div>

      <div className="ds-tabs" role="tablist" aria-label="Categorías de estadísticas">
        <button
          type="button"
          role="tab"
          id="ds-tab-performance"
          aria-selected={activeTab === "performance"}
          aria-controls="ds-tabpanel-performance"
          className={`ds-tab ${activeTab === "performance" ? "is-active" : ""}`}
          onClick={() => setActiveTab("performance")}
        >
          <span>Rendimiento global</span>
        </button>
        <button
          type="button"
          role="tab"
          id="ds-tab-doubts"
          aria-selected={activeTab === "doubts"}
          aria-controls="ds-tabpanel-doubts"
          className={`ds-tab ${activeTab === "doubts" ? "is-active" : ""}`}
          onClick={() => setActiveTab("doubts")}
        >
          <span>Estrategia y examen</span>
        </button>
      </div>

      {activeTab === "performance" && (
        <div
          id="ds-tabpanel-performance"
          role="tabpanel"
          aria-labelledby="ds-tab-performance"
          className="ds-tabpanel ds-grid"
        >
          {/* Card 1: Percentil */}
          <StatCard
            icon={<LeaderboardIcon sx={{ fontSize: 34 }} />}
            title="¿Qué percentil medio he obtenido?"
            endpoint={progress}
            available={hasProgressEvidence}
            value={progress.data?.percentile}
            format={fmtPercentile}
            unit="%"
            expanded={expandedCards.percentile}
            onToggleExpand={() => toggleExpand("percentile")}
          >
            <div className="ds-expansion-content">
              <div className="ds-percentile-bar-wrap">
                <div className="ds-percentile-track">
                  <div
                    className="ds-percentile-fill"
                    style={{ width: `${Math.min(100, Math.max(0, progress.data?.percentile || 0))}%` }}
                  />
                </div>
                <div className="ds-percentile-labels">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              <p className="ds-detail-note">
                El percentil compara tu rendimiento frente a tus compañeros. Si tienes un 80%, significa que estás por encima del 80% de los estudiantes.
              </p>
            </div>
          </StatCard>

          {/* Card 2: Nota media */}
          <StatCard
            icon={<MilitaryTechIcon sx={{ fontSize: 34 }} />}
            title="¿Cuál es mi nota media?"
            endpoint={progress}
            available={hasProgressEvidence}
            value={progress.data?.gpa}
            format={fmtGpa}
            unit="/ 10"
            expanded={expandedCards.gpa}
            onToggleExpand={() => toggleExpand("gpa")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-correct">
                  <span className="ds-pill-num">{progress.data?.acertadas || 0}</span>
                  <span className="ds-pill-label">Acertadas</span>
                </div>
                <div className="ds-stat-pill is-wrong">
                  <span className="ds-pill-num">{progress.data?.falladas || 0}</span>
                  <span className="ds-pill-label">Falladas</span>
                </div>
                <div className="ds-stat-pill is-accuracy">
                  <span className="ds-pill-num">
                    {answeredCount > 0
                      ? `${Math.round(((progress.data?.acertadas || 0) / answeredCount) * 100)}%`
                      : "0%"}
                  </span>
                  <span className="ds-pill-label">Precisión</span>
                </div>
              </div>
            </div>
          </StatCard>

          {/* Card 3: Tests realizados */}
          <StatCard
            icon={<FactCheckIcon sx={{ fontSize: 34 }} />}
            title="¿Cuántos test he realizado?"
            endpoint={speed}
            available={hasTests}
            value={testsDone}
            format={(v) => v}
            unit="tests"
            expanded={expandedCards.tests}
            onToggleExpand={() => toggleExpand("tests")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{speed.data?.my_answered || 0}</span>
                  <span className="ds-pill-label">Preguntas respondidas</span>
                </div>
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">
                    {testsDone > 0
                      ? Math.round((speed.data?.my_answered || 0) / testsDone)
                      : 0}
                  </span>
                  <span className="ds-pill-label">Promedio por test</span>
                </div>
              </div>
            </div>
          </StatCard>

          {/* Card 4: Velocidad */}
          <StatCard
            icon={<BoltIcon sx={{ fontSize: 34 }} />}
            title="¿Contesto preguntas demasiado lento?"
            tooltip="Comparado con el promedio global/cohorte"
            endpoint={speed}
            available={hasSpeedEvidence}
            value={speed.data?.my_avg_seconds}
            format={fmtSeconds}
            unit="seg."
            inlineContextNote
            contextNote={
              <p className="ds-comparison-copy">
                Promedio global/cohorte: {fmtSeconds(speed.data?.tier_avg_seconds || 0)} seg.
              </p>
            }
            expanded={expandedCards.speed}
            onToggleExpand={() => toggleExpand("speed")}
          >
            <div className="ds-expansion-content">
              <div className="ds-speed-wrap">
                <SpeedChart
                  mine={speed.data?.my_avg_seconds}
                  cohort={speed.data?.tier_avg_seconds}
                />
                <div className="ds-speed-legend">
                  <span className="ds-speed-item">
                    <span className="ds-legend-person" aria-hidden="true">●</span>
                    <span className="ds-speed-num">
                      {fmtSeconds(speed.data?.my_avg_seconds)}
                      <span className="ds-speed-unit">seg. (Tú)</span>
                    </span>
                  </span>
                  <span className="ds-speed-item">
                    <span className="ds-legend-cohort" aria-hidden="true" />
                    <span className="ds-speed-num">
                      {fmtSeconds(speed.data?.tier_avg_seconds)}
                      <span className="ds-speed-unit">seg. (Cohorte)</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </StatCard>
        </div>
      )}

      {activeTab === "doubts" && (
        <div
          id="ds-tabpanel-doubts"
          role="tabpanel"
          aria-labelledby="ds-tab-doubts"
          className="ds-tabpanel ds-grid"
        >
          {/* Card 1: Dudas resueltas (Me la Juego) */}
          <StatCard
            icon={<EmojiObjectsOutlinedIcon sx={{ fontSize: 34, color: "#ecb236" }} />}
            title="¿Acierto cuando me la juego?"
            tooltip="Porcentaje de acierto en aquellas preguntas donde activaste «Me la Juego»"
            endpoint={doubtStats}
            available={hasDoubtEvidence}
            value={doubtSuccessRate}
            format={fmtPercentile}
            unit="%"
            contextNote={
              <p className="ds-comparison-copy">
                {doubtsResolved > 0
                  ? `${doubtsCorrect} de ${doubtsResolved} preguntas resueltas acertadas`
                  : `${doubtsPending} preguntas pendientes de resolver`}
              </p>
            }
            expanded={expandedCards.doubts}
            onToggleExpand={() => toggleExpand("doubts")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-correct">
                  <span className="ds-pill-num">{doubtsCorrect}</span>
                  <span className="ds-pill-label">Acertadas</span>
                </div>
                <div className="ds-stat-pill is-wrong">
                  <span className="ds-pill-num">{doubtsIncorrect}</span>
                  <span className="ds-pill-label">Falladas</span>
                </div>
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{doubtsPending}</span>
                  <span className="ds-pill-label">Pendientes</span>
                </div>
              </div>
              <p className="ds-detail-note">
                {(doubtStats.data?.advice_key && ADVICE_MAP[doubtStats.data.advice_key]) ||
                  doubtStats.data?.advice_message ||
                  (doubtSuccessRate >= 50
                    ? "Tu intuición suele estar bien encaminada al jugártela. Arriesgarte en preguntas donde dudas suele sumarte puntos."
                    : "Tus dudas suelen traducirse en errores al jugártela. Te recomendamos prudencia antes de arriesgar.")}
              </p>
            </div>
          </StatCard>

          {/* Card 2: Frecuencia de «Me la Juego» */}
          <StatCard
            icon={<HelpOutlineIcon sx={{ fontSize: 34 }} />}
            title="¿Con qué frecuencia me la juego?"
            tooltip="Porcentaje de preguntas marcadas con «Me la Juego» sobre el total respondido"
            endpoint={doubtStats}
            available={hasProgressEvidence || doubtsMarked > 0}
            value={doubtFrequencyPct}
            format={fmtPercentile}
            unit="%"
            contextNote={
              <p className="ds-comparison-copy">
                {doubtsMarked} {doubtsMarked === 1 ? "pregunta marcada" : "preguntas marcadas"} con «Me la Juego»
              </p>
            }
            expanded={expandedCards.doubtFrequency}
            onToggleExpand={() => toggleExpand("doubtFrequency")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{doubtsMarked}</span>
                  <span className="ds-pill-label">Total marcadas</span>
                </div>
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{doubtFrequencyPct}%</span>
                  <span className="ds-pill-label">Tasa de juego</span>
                </div>
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{doubtsPerTest}</span>
                  <span className="ds-pill-label">Jugadas por test</span>
                </div>
              </div>
              <p className="ds-detail-note">
                {doubtFrequencyPct <= 15
                  ? "Mantienes una tasa prudente al jugártela, arriesgando solo en momentos clave."
                  : "Te la juegas con frecuencia. Revisar tu tasa de acierto te ayudará a calibrar si conviene arriesgar tanto."}
              </p>
            </div>
          </StatCard>

          {/* Card 3: Precisión global */}
          <StatCard
            icon={<GpsFixedIcon sx={{ fontSize: 34 }} />}
            title="¿Cuál es mi precisión global?"
            tooltip="Porcentaje de acierto sobre las preguntas que decides contestar"
            endpoint={progress}
            available={hasProgressEvidence}
            value={accuracyPct}
            format={fmtPercentile}
            unit="%"
            contextNote={
              <p className="ds-comparison-copy">
                {acertadas} correctas de {answeredCount} preguntas respondidas
              </p>
            }
            expanded={expandedCards.precision}
            onToggleExpand={() => toggleExpand("precision")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-correct">
                  <span className="ds-pill-num">{acertadas}</span>
                  <span className="ds-pill-label">Aciertos</span>
                </div>
                <div className="ds-stat-pill is-wrong">
                  <span className="ds-pill-num">{falladas}</span>
                  <span className="ds-pill-label">Fallos</span>
                </div>
                <div className="ds-stat-pill is-accuracy">
                  <span className="ds-pill-num">{accuracyPct}%</span>
                  <span className="ds-pill-label">Efectividad</span>
                </div>
              </div>
              <p className="ds-detail-note">
                {accuracyPct >= 75
                  ? "Excelente puntería. Arriesgarte cuando dudas o te la juegas tiene un valor esperado positivo para tu nota."
                  : "Tu precisión aconseja prudencia. En preguntas donde no puedas descartar alternativas, considera no arriesgar."}
              </p>
            </div>
          </StatCard>

          {/* Card 4: Ratio de aciertos por fallo */}
          <StatCard
            icon={<BalanceIcon sx={{ fontSize: 34 }} />}
            title="¿Cuántas acierto por cada fallo?"
            tooltip="Número de preguntas que aciertas por cada error cometido"
            endpoint={progress}
            available={hasProgressEvidence}
            value={ratioAciertosFallo}
            format={(v) => v}
            unit="aciertos / fallo"
            contextNote={
              <p className="ds-comparison-copy">
                {falladas > 0
                  ? `Compensas cada error con ${ratioAciertosFallo} respuestas correctas`
                  : "¡Historial impecable sin fallos registrados!"}
              </p>
            }
            expanded={expandedCards.ratio}
            onToggleExpand={() => toggleExpand("ratio")}
          >
            <div className="ds-expansion-content">
              <div className="ds-stat-pills">
                <div className="ds-stat-pill is-correct">
                  <span className="ds-pill-num">{acertadas}</span>
                  <span className="ds-pill-label">Aciertos totales</span>
                </div>
                <div className="ds-stat-pill is-wrong">
                  <span className="ds-pill-num">{falladas}</span>
                  <span className="ds-pill-label">Fallos totales</span>
                </div>
                <div className="ds-stat-pill is-general">
                  <span className="ds-pill-num">{ratioAciertosFallo}</span>
                  <span className="ds-pill-label">Factor acierto</span>
                </div>
              </div>
              <p className="ds-detail-note">
                En la mayoría de oposiciones cada 3 fallos restan 1 acierto. Un ratio superior a 3,0 garantiza que tus errores nunca consuman tu nota neta.
              </p>
            </div>
          </StatCard>
        </div>
      )}
    </section>
  );
};

export default DetailedStats;
